import { Injectable } from "@angular/core";

type WindowWithGTM = Window & {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
};

@Injectable({
    providedIn: "root"
})
export class GoogleTagManagerService {
    private readonly GTM_ID = "G-FF4CZKWFBC";
    private readonly GTM_SCRIPT_SRC = `https://www.googletagmanager.com/gtag/js?id=${this.GTM_ID}`;
    private injectedScripts: HTMLScriptElement[] = [];
    private isInjected = false;

    private isBrowser(): boolean {
        return typeof document !== "undefined" && typeof window !== "undefined";
    }

    private getWindow(): WindowWithGTM {
        return window as WindowWithGTM;
    }

    public injectGTM(): void {
        if (!this.isBrowser()) {
            return;
        }

        if (this.isInjected) {
            return;
        }

        const win = this.getWindow();

        // Initialize dataLayer
        if (!win.dataLayer) {
            win.dataLayer = [];
        }

        // Create and inject the async script tag
        const asyncScript = document.createElement("script");
        asyncScript.async = true;
        asyncScript.src = this.GTM_SCRIPT_SRC;
        asyncScript.id = "google-tag-manager-async";
        document.head.appendChild(asyncScript);
        this.injectedScripts.push(asyncScript);

        // Create and inject the inline initialization script
        const inlineScript = document.createElement("script");
        inlineScript.id = "google-tag-manager-inline";
        inlineScript.textContent = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${this.GTM_ID}');
        `;
        document.head.appendChild(inlineScript);
        this.injectedScripts.push(inlineScript);

        this.isInjected = true;
    }

    public removeGTM(): void {
        if (!this.isBrowser()) {
            return;
        }

        // Remove all injected scripts
        this.injectedScripts.forEach((script) => {
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
        });
        this.injectedScripts = [];

        const win = this.getWindow();

        // Clear dataLayer and gtag function
        if (win.dataLayer) {
            win.dataLayer = [];
        }
        if (win.gtag) {
            delete win.gtag;
        }

        this.isInjected = false;
    }

    public getIsInjected(): boolean {
        return this.isInjected;
    }
}
